import { useState } from 'react'
import type { TokenBalance } from '@properwallet/chain-eos'
import { formatUsd } from '@properwallet/chain-eos'
import { useTranslation } from '../hooks/useTranslation'
import InfoTip from './InfoTip'

interface TokenCardProps {
  token: TokenBalance
  onSend?: () => void
  /** Opens WebDEX with this token pre-selected. */
  onSwap?: () => void
  highlighted?: boolean
  onHide?: () => void
  /** Icon shown in the hide/unhide button — '✕' to hide, '+' to unhide. Defaults to '✕'. */
  hideLabel?: string
}

export default function TokenCard({ token, onSend, onSwap, highlighted, onHide, hideLabel = '✕' }: TokenCardProps) {
  const { t } = useTranslation()
  const [imgFailed, setImgFailed] = useState(false)
  const isVaulta = token.symbol === 'A'
  const isEos = token.symbol === 'EOS'

  const iconColor = isVaulta
    ? 'from-brand to-brand-dark'
    : isEos
      ? 'from-blue-500 to-cyan-600'
      : 'from-gray-500 to-gray-600'

  const showUrl = Boolean(token.logoUrl && !imgFailed)
  const letter = token.symbol.charAt(0).toUpperCase()
  const hasUsd = token.usdValue !== undefined

  return (
    <div
      className={`card relative flex items-center gap-3 ${
        highlighted ? 'border border-brand/40 bg-brand/5' : ''
      }`}
    >
      {onHide && (
        <button
          onClick={onHide}
          title={hideLabel === '+' ? 'Unhide token' : 'Hide token'}
          className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full flex items-center justify-center text-[11px] text-gray-500 hover:text-gray-300 hover:bg-surface-2 transition-colors"
          style={{ background: 'none', border: 'none', cursor: 'pointer' }}
        >
          {hideLabel}
        </button>
      )}
      {/* Token icon */}
      <div className="relative w-10 h-10 flex-shrink-0">
        {showUrl ? (
          <img
            src={token.logoUrl}
            alt=""
            className="w-10 h-10 rounded-full object-cover bg-surface-2"
            onError={() => setImgFailed(true)}
          />
        ) : (
          <div
            className={`w-10 h-10 rounded-full bg-gradient-to-br ${iconColor} flex items-center justify-center`}
          >
            <span className="text-white font-bold text-sm">{letter}</span>
          </div>
        )}
      </div>

      {/* Token info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="font-semibold text-white text-sm">{token.name}</span>
          {token.isKnown === false && <InfoTip text={t('token.unknownWarning')} />}
          {isVaulta && (
            <span className="text-[10px] bg-brand/20 text-brand-light px-1.5 py-0.5 rounded-full font-medium">
              Native
            </span>
          )}
        </div>
        <p className="text-xs text-gray-400">
          {token.symbol}
          {token.contract && (
            <span className="text-gray-600"> · {token.contract}</span>
          )}
        </p>
      </div>

      {/* Balance */}
      <div className="text-right flex-shrink-0">
        <p className="font-semibold text-white text-sm">
          {parseFloat(token.amount).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 4,
          })}
        </p>
        {hasUsd ? (
          <p className="text-xs text-green-400/80">
            {formatUsd(token.usdValue!)}
          </p>
        ) : (
          <p className="text-xs text-gray-400">{token.symbol}</p>
        )}
      </div>

      {/* Swap button — opens WebDEX with this token in the "you pay" slot */}
      {onSwap && (
        <button
          onClick={onSwap}
          title={t('wallet.swap')}
          className="ml-1 w-8 h-8 rounded-lg bg-brand/10 text-brand-light flex items-center justify-center text-sm hover:bg-brand/25 transition-colors flex-shrink-0"
        >
          ⇄
        </button>
      )}

      {/* Send button */}
      {onSend && (
        <button
          onClick={onSend}
          className="ml-1 w-8 h-8 rounded-lg bg-brand/20 text-brand-light flex items-center justify-center text-sm hover:bg-brand/30 transition-colors flex-shrink-0"
        >
          ↑
        </button>
      )}
    </div>
  )
}
